"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.defineReceiver = exports.defineCommand = void 0;
var tslib_1 = require("tslib");
var consola_1 = tslib_1.__importDefault(require("consola"));
var runtime_context_1 = require("@drapejs/runtime-context");
var queue_1 = tslib_1.__importDefault(require("./queue"));
/**
 * Create an invoker to invoke commands with
 * @param worker If set, creates a proxy invoker that will invoke commands on the remove Web Worker, assuming there's an invoker instance there to receive the commands.
 */
function createInvoker(worker) {
    if (worker) {
        return new InvokerProxy(worker);
    }
    return new InvokerWorker();
}
exports.default = createInvoker;
/**
 * This type of invoker acts as a proxy when invoking commands in a Web Worker.
 */
var InvokerProxy = /** @class */ (function () {
    /**
     *
     * @param worker A Web Worker reference where we expect a BrowserWorker invoker instance to run.
     */
    function InvokerProxy(worker) {
        var _this = this;
        this.isReady = false;
        this.worker = worker;
        this.readyPromise = new Promise(function (resolve) {
            var listener = function (m) {
                if (m.data === 'READY') {
                    consola_1.default.trace("Invoker (Proxy): Received 'READY' from remote worker");
                    resolve(true);
                    _this.worker.removeEventListener('message', listener);
                }
            };
            _this.worker.addEventListener('message', listener);
            // Send a PING to trigger a response in case remote worker initialized before proxy
            _this.worker.postMessage('PING');
        });
    }
    InvokerProxy.prototype.configureCommand = function (command) {
        throw new Error("configureCommand is not supported on Invoker proxy");
    };
    InvokerProxy.prototype.invoke = function (type, command) {
        var _this = this;
        validateCommand(type);
        return new Promise(function (resolve, reject) {
            _this.readyPromise.then(function () {
                var channel = new MessageChannel();
                channel.port2.onmessage = function (result) {
                    var _a;
                    if ((_a = result.data) === null || _a === void 0 ? void 0 : _a.__exception) {
                        reject(result.data.__exception);
                    }
                    resolve(result.data);
                };
                _this.worker.postMessage(tslib_1.__assign({ __commandType: type.name }, command), [channel.port1]);
            });
        });
    };
    return InvokerProxy;
}());
/**
 * This is an actual invoker. If created in a Web Worker, it will also listen for messages from Proxy.
 */
var InvokerWorker = /** @class */ (function () {
    function InvokerWorker() {
        var _this = this;
        this.commandTypes = {};
        this.queues = {};
        if ((0, runtime_context_1.isWorker)()) {
            self.addEventListener('message', function (m) { return tslib_1.__awaiter(_this, void 0, void 0, function () {
                var commandType, command, replyPort_1, chain;
                return tslib_1.__generator(this, function (_a) {
                    if (m.data === 'PING') {
                        self.postMessage('READY');
                    }
                    if (m.data.__commandType) {
                        consola_1.default.trace('Invoker: Got invoke request of command type', {
                            type: m.data.__commandType
                        });
                        commandType = m.data.__commandType;
                        command = m.data;
                        replyPort_1 = m.ports[0];
                        chain = this.createChain(command, commandType, function (value) {
                            if (!replyPort_1) {
                                return;
                            }
                            replyPort_1.postMessage(value);
                        }, function (error) {
                            if (!replyPort_1) {
                                return;
                            }
                            replyPort_1.postMessage({
                                __exception: error
                            });
                        });
                        chain.next();
                    }
                    return [2 /*return*/];
                });
            }); });
            consola_1.default.trace('Invoker: Event listener registered');
            self.postMessage('READY');
        }
    }
    InvokerWorker.prototype.createChain = function (command, commandType, onSuccess, onFail) {
        var queues = this.queues;
        var invoker = this;
        var chain = {
            command: command,
            commandType: commandType,
            receivers: this.getReceiversFor(commandType),
            result: {},
            onSuccess: onSuccess,
            onFail: onFail,
            next: function () {
                if (chain.receivers.length == 0) {
                    chain.onSuccess(chain.result);
                    return;
                }
                var current = chain.receivers[0];
                var queue;
                if (current.queueId) {
                    if (!queues[current.queueId]) {
                        queue = new queue_1.default(current.queueId, invoker);
                        queues[current.queueId] = queue;
                    }
                    else {
                        queue = queues[current.queueId];
                    }
                }
                else {
                    queue = new queue_1.default('_', invoker);
                }
                queue.process(chain);
            }
        };
        return chain;
    };
    InvokerWorker.prototype.invoke = function (type, command) {
        var _this = this;
        validateCommand(type);
        return new Promise(function (resolve, reject) {
            var chain = _this.createChain(command, type.name, resolve, reject);
            try {
                chain.next();
            }
            catch (err) {
                reject(err);
            }
        });
    };
    InvokerWorker.prototype.configureCommand = function (command) {
        var receivers = this.commandTypes[command.name];
        if (!receivers) {
            receivers = [];
            this.commandTypes[command.name] = receivers;
        }
        // consola.trace("Invoker: Configure command", {
        //   name: command.name
        // });
        var configuration = {
            addReceiver: function (receiver) {
                receivers.push(receiver);
                return configuration;
            }
        };
        return configuration;
    };
    /**
     * Returns an array of the receiver registered at the time of calling.
     * @param commandType The command type to fetch receivers for
     */
    InvokerWorker.prototype.getReceiversFor = function (commandType) {
        var receivers = this.commandTypes[commandType];
        if (!receivers) {
            throw "No receivers found for command ".concat(commandType);
        }
        return receivers.slice();
    };
    return InvokerWorker;
}());
function defineCommand(name) {
    return {
        name: name,
    };
}
exports.defineCommand = defineCommand;
/**
 * Defines a new receiver for a command type.
 * @param receiver The function that receives and performs the command
 * @param queueId The queue id could be used to control concurrency and race conditions. The Invoker guarantees that only a single command from the same queue executes at any given time.
 */
function defineReceiver(commandType, receiver, queueId) {
    receiver.queueId = queueId;
    return receiver;
}
exports.defineReceiver = defineReceiver;
function validateCommand(type) {
    if (!type) {
        throw 'invoke() called with null or undefined command type';
    }
    else if (!type.name) {
        throw 'invoke() called with invalid command type, ensure you\'re using defineCommand() to create commands';
    }
}
//# sourceMappingURL=index.js.map